import type { FC } from 'react';
import React, { useEffect } from 'react';
import { css } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AiGenerativeAudioBriefingIcon from '@atlaskit/icon-lab/core/ai-generative-audio-briefing';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import Inline from '@atlaskit/primitives/inline';

import { useAudioListenTrigger } from '@atlassian/ai-audio/hooks/use-audio-listen-trigger';

import { useActiveContent } from '../../hooks/useActiveContent.ts';
import { useAudioContentQuery } from '../platform/useAudioContentQuery.tsx';
import { useAudioButtonWrapperActions } from '../../store/useAudioButtonWrapperStore.tsx';

export type ListenBylineProps = {
	contentId: string;
	contentType?: string;
	playImmediately?: boolean;
};

const i18n = defineMessages({
	listen: {
		id: 'audio.listen-byline.listen',
		defaultMessage: 'Listen',
		description: 'Text for the listen button in the byline for audio',
	},
});

const wrapperStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	marginInlineEnd: token('space.050'),
	height: '24px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& button': {
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		borderRadius: token('radius.small'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& button:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

export const ListenByline: FC<ListenBylineProps> = ({
	contentId,
	contentType,
	playImmediately = false,
}: ListenBylineProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data } = useAudioContentQuery({ contentId });
	const { setActiveContent } = useAudioButtonWrapperActions();
	const activeContent = useActiveContent({
		contentId,
		contentType: contentType ?? 'page',
		data,
	});

	useEffect(() => {
		setActiveContent(activeContent);
	}, [activeContent, setActiveContent]);

	const { handleClick, isAudioSupported, tooltipContent, buttonRef } = useAudioListenTrigger({
		activeContent,
		source: 'byLine',
		isAutoOpenDisabled: !data?.singleContent,
		playImmediately,
	});

	const handleHover = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'byLine',
				action: 'hovered',
				actionSubject: 'button',
				actionSubjectId: 'byLineListen',
				attributes: {
					contentType,
				},
			},
		}).fire();
	};

	return (
		<span css={wrapperStyles}>
			<Tooltip content={tooltipContent} position="bottom">
				<Pressable
					ref={buttonRef}
					onClick={handleClick}
					onMouseEnter={handleHover}
					onFocus={handleHover}
					isDisabled={!isAudioSupported}
					testId="byline-listen"
				>
					<Inline space="space.050" alignBlock="center">
						<AiGenerativeAudioBriefingIcon label="" />
						{formatMessage(i18n.listen)}
					</Inline>
				</Pressable>
			</Tooltip>
		</span>
	);
};
